import { useEffect, useRef, useCallback } from "react";

// ─── Fallback Meta Embedded Signup Configuration ────────────────────────────
const META_CONFIG_ID = process.env.REACT_APP_META_WHATSAPP_CONFIG_ID || "1229146472634257";  // <-- your Configuration ID
// ─────────────────────────────────────────────────────────────────────────────

const isPermittedOrigin = (origin) => {
  try {
    const url = new URL(origin);
    const host = url.hostname.toLowerCase();
    return (
      url.protocol === "https:" &&
      (host === "facebook.com" ||
        host.endsWith(".facebook.com") ||
        host === "whatsapp.com" ||
        host.endsWith(".whatsapp.com") ||
        host === "meta.com" ||
        host.endsWith(".meta.com"))
    );
  } catch {
    return false;
  }
};
const META_SIGNUP_TIMEOUT_MS = Number(process.env.REACT_APP_META_SIGNUP_TIMEOUT_MS || 120000);

// Polls window._fbReady — set to true inside fbAsyncInit in index.html
// only after FB.init() has fully completed.
function waitForFBReady() {
  return new Promise((resolve) => {
    if (window._fbReady) { resolve(); return; }
    const poll = setInterval(() => {
      if (window._fbReady) { clearInterval(poll); resolve(); }
    }, 100);
  });
}

function collectPayloadObjects(raw, seen = new Set()) {
  if (!raw || typeof raw !== "object" || seen.has(raw)) return [];
  seen.add(raw);

  if (Array.isArray(raw)) {
    return raw.flatMap((item) => collectPayloadObjects(item, seen));
  }

  const nestedKeys = ["data", "payload", "event_data", "eventData", "session_info", "sessionInfo"];
  return [
    raw,
    ...nestedKeys.flatMap((key) => collectPayloadObjects(raw[key], seen)),
  ];
}

function pickPayloadValue(payloads, ...keys) {
  for (const payload of payloads) {
    for (const key of keys) {
      const value = payload?.[key];
      if (value !== undefined && value !== null && value !== "") return value;
    }
  }
  return "";
}

function payloadEventTokens(payloads) {
  return payloads
    .flatMap((payload) => [payload.type, payload.event, payload.status])
    .filter((value) => value !== undefined && value !== null && value !== "")
    .map((value) => String(value).toUpperCase());
}

export function isMetaSignupPayload(raw) {
  const payloads = collectPayloadObjects(raw);
  const tokens = payloadEventTokens(payloads);
  return (
    tokens.some((token) => token === "WA_EMBEDDED_SIGNUP") ||
    tokens.some((token) => [
      "FINISH",
      "COMPLETE",
      "COMPLETED",
      "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING",
      "FINISH_ONLY_WABA",
      "FINISH_OBO_MIGRATION",
      "FINISH_GRANT_ONLY_API_ACCESS",
      "CANCEL",
      "CANCELLED",
      "ERROR",
      "POPUP_CLOSED"
    ].includes(token)) ||
    !!pickPayloadValue(payloads, "waba_id", "wabaId", "wabaID", "waba", "phone_number_id", "phoneNumberId", "phone_id", "phoneId", "business_id", "businessId", "businessID")
  );
}

export function normalise(raw) {
  if (!raw || typeof raw !== "object") return { event: "ignored", data: {} };

  const payloads = collectPayloadObjects(raw);
  const tokens = payloadEventTokens(payloads);

  if (tokens.some((token) => ["CANCEL", "CANCELLED"].includes(token))) {
    return { event: "cancelled", data: {} };
  }
  if (tokens.some((token) => token === "POPUP_CLOSED")) {
    return { event: "popup_closed", data: {} };
  }
  if (tokens.some((token) => token === "ERROR")) {
    return { event: "error", data: {} };
  }

  const wabaId = pickPayloadValue(payloads, "waba_id", "wabaId", "wabaID", "waba");
  const phoneNumberId = pickPayloadValue(payloads, "phone_number_id", "phoneNumberId", "phone_id", "phoneId");
  const businessId = pickPayloadValue(payloads, "business_id", "businessId", "businessID");

  const completionTokens = [
    "FINISH",
    "COMPLETE",
    "COMPLETED",
    "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING",
    "FINISH_ONLY_WABA",
    "FINISH_OBO_MIGRATION",
    "FINISH_GRANT_ONLY_API_ACCESS",
  ];

  const matchedCompletionToken = tokens.find((token) => completionTokens.includes(token));

  if (
    matchedCompletionToken ||
    !!(wabaId || phoneNumberId)
  ) {
    return {
      event: "completed",
      data: {
        waba_id: wabaId,
        phone_number_id: phoneNumberId,
        business_id: businessId,
        meta_event: matchedCompletionToken || "FINISH",
      },
    };
  }

  return { event: "ignored", data: {} };
}

function hasPlainObjectValue(value) {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function pickFirst(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== "");
}

export function buildLoginOptions(launch = {}, context = {}) {
  const source = hasPlainObjectValue(launch) ? launch : {};
  const rawLoginOptions = source.login_options || source.loginOptions || source.options;
  const loginOptions = hasPlainObjectValue(rawLoginOptions) ? rawLoginOptions : source;

  // Extract explicit or inferred onboarding mode (supports camelCase and snake_case)
  const rawMode = pickFirst(
    loginOptions.mode,
    loginOptions.onboarding_mode,
    loginOptions.onboardingMode,
    source.mode,
    source.onboarding_mode,
    source.onboardingMode,
    context?.mode,
    context?.onboarding_mode,
    context?.onboardingMode
  );

  const rawFeatureType = pickFirst(
    loginOptions.feature_type,
    loginOptions.featureType,
    source.feature_type,
    source.featureType,
    context?.feature_type,
    context?.featureType
  );

  const rawSessionInfoVersion = pickFirst(
    loginOptions.session_info_version,
    loginOptions.sessionInfoVersion,
    source.session_info_version,
    source.sessionInfoVersion,
    context?.session_info_version,
    context?.sessionInfoVersion
  );

  const backendExtras = pickFirst(loginOptions.extras, source.extras, context?.extras) || {};
  const extrasFeatureType = pickFirst(backendExtras?.featureType, backendExtras?.feature_type);
  const extrasSessionInfoVersion = pickFirst(backendExtras?.sessionInfoVersion, backendExtras?.session_info_version);

  const isCoexistence =
    String(rawMode).toLowerCase() === "coexistence" ||
    rawFeatureType === "whatsapp_business_app_onboarding" ||
    extrasFeatureType === "whatsapp_business_app_onboarding";

  const setup = pickFirst(loginOptions.setup, source.setup, context?.setup, {});

  const configId = pickFirst(
    loginOptions.config_id,
    loginOptions.configId,
    source.config_id,
    source.configId,
    context?.config_id,
    context?.configId,
    META_CONFIG_ID
  );

  const responseType = pickFirst(
    loginOptions.response_type,
    loginOptions.responseType,
    source.response_type,
    source.responseType,
    context?.response_type,
    context?.responseType,
    "code"
  );

  const overrideDefaultResponseType =
    pickFirst(
      loginOptions.override_default_response_type,
      loginOptions.overrideDefaultResponseType,
      source.override_default_response_type,
      source.overrideDefaultResponseType,
      context?.override_default_response_type,
      context?.overrideDefaultResponseType
    ) ?? true;

  let resolvedExtras;

  if (isCoexistence) {
    // ─── WhatsApp Coexistence onboarding mode (Meta Embedded Signup v4) ──────
    // Allows connecting an existing WhatsApp Business App number while keeping
    // the mobile app active on the same number.
    resolvedExtras = {
      setup,
      ...backendExtras,
      featureType: "whatsapp_business_app_onboarding",
    };

    // If sessionInfoVersion was explicitly supplied by backend/extras, preserve it;
    // for standard v4 coexistence flow without explicit sessionInfoVersion, do not force legacy '3'.
    const explicitSessionInfoVersion = pickFirst(extrasSessionInfoVersion, rawSessionInfoVersion);
    if (
      explicitSessionInfoVersion !== undefined &&
      explicitSessionInfoVersion !== null &&
      explicitSessionInfoVersion !== ""
    ) {
      resolvedExtras.sessionInfoVersion = Number(explicitSessionInfoVersion);
    }
  } else {
    // ─── Standard Cloud API onboarding mode ─────────────────────────────────
    resolvedExtras = {
      setup,
      ...backendExtras,
      featureType: pickFirst(
        extrasFeatureType,
        rawFeatureType,
        "whatsapp_embedded_signup"
      ),
      sessionInfoVersion: (() => {
        const val = pickFirst(extrasSessionInfoVersion, rawSessionInfoVersion);
        return val !== undefined && val !== null && val !== "" ? Number(val) : 3;
      })(),
    };
  }

  return {
    config_id: configId,
    response_type: responseType,
    override_default_response_type: overrideDefaultResponseType,
    extras: resolvedExtras,
  };
}

export function useMetaSignup(onResult, onComplete) {
  const timeoutRef = useRef(null);
  const submitGraceTimerRef = useRef(null);
  const popupCheckRef = useRef(null);
  const popupWindowRef = useRef(null);
  const originalWindowOpenRef = useRef(null);
  const terminalEventRef = useRef(false);
  const sessionRef = useRef({
    docliftOnboardingSessionId: null,
    docliftSessionToken: null,
    expiresAt: null,
    authCode: null,
    signupData: null,
    submitted: false,
    mode: null,
    isCoexistence: false,
  });

  const cleanup = useCallback(() => {
    console.log("[MetaSignup DEBUG] cleanup() called");
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (submitGraceTimerRef.current) {
      window.clearTimeout(submitGraceTimerRef.current);
      submitGraceTimerRef.current = null;
    }
    if (popupCheckRef.current) {
      window.clearInterval(popupCheckRef.current);
      popupCheckRef.current = null;
    }
    if (originalWindowOpenRef.current && window.open !== originalWindowOpenRef.current) {
      window.open = originalWindowOpenRef.current;
      originalWindowOpenRef.current = null;
    }
    popupWindowRef.current = null;
    sessionRef.current = {
      docliftOnboardingSessionId: null,
      docliftSessionToken: null,
      expiresAt: null,
      authCode: null,
      signupData: null,
      submitted: false,
      mode: null,
      isCoexistence: false,
    };
  }, []);

  const emitTerminalResult = useCallback((result) => {
    console.log("[MetaSignup DEBUG] emitTerminalResult:", result);
    if (terminalEventRef.current) return;
    terminalEventRef.current = true;
    onResult?.(result);
    cleanup();
  }, [cleanup, onResult]);

  const startTimeout = useCallback(() => {
    if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    timeoutRef.current = window.setTimeout(() => {
      timeoutRef.current = null;
      console.warn("[MetaSignup DEBUG] Signup timeout expired:", META_SIGNUP_TIMEOUT_MS);
      emitTerminalResult({ event: "timeout", data: {} });
    }, META_SIGNUP_TIMEOUT_MS);
  }, [emitTerminalResult]);

  // ─── Emit result once Meta signals have arrived ───────────────────────────
  const trySubmit = useCallback(async ({ allowWithoutAuthCode = false, allowWithoutSignupData = false } = {}) => {
    const s = sessionRef.current;
    const isCoexistence =
      s.isCoexistence ||
      s.mode === "coexistence" ||
      s.signupData?.meta_event === "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING";

    const hasAuth = !!s.authCode;
    const hasSignup = !!s.signupData;

    // A submission is ready if:
    // 1) Both authCode and signupData are present
    // 2) authCode is present AND (allowWithoutSignupData || popup is closed || isCoexistence)
    // 3) signupData is present AND (isCoexistence || allowWithoutAuthCode)
    const isPopupClosed = !popupWindowRef.current || popupWindowRef.current?.closed;
    const canSubmitWithAuthOnly = hasAuth && (allowWithoutSignupData || isPopupClosed || isCoexistence);
    const canSubmitWithSignupOnly = hasSignup && (isCoexistence || allowWithoutAuthCode);
    const hasBoth = hasAuth && hasSignup;

    const isReady = hasBoth || canSubmitWithAuthOnly || canSubmitWithSignupOnly;

    console.log("[MetaSignup DEBUG] trySubmit entered:", {
      allowWithoutAuthCode,
      allowWithoutSignupData,
      isCoexistence,
      hasAuth,
      hasSignup,
      isReady,
      submitted: s.submitted,
      terminalEvent: terminalEventRef.current,
      onboardingSessionId: s.docliftOnboardingSessionId,
    });

    if (terminalEventRef.current || !isReady || s.submitted) {
      console.warn("[MetaSignup DEBUG] trySubmit not proceeding:", {
        terminalEvent: terminalEventRef.current,
        isReady,
        hasAuth,
        hasSignup,
        alreadySubmitted: s.submitted,
      });
      return;
    }
    s.submitted = true;

    if (submitGraceTimerRef.current) {
      window.clearTimeout(submitGraceTimerRef.current);
      submitGraceTimerRef.current = null;
    }

    const expiresAtMs = s.expiresAt ? new Date(s.expiresAt).getTime() : null;
    if (expiresAtMs && Number.isFinite(expiresAtMs) && Date.now() >= expiresAtMs) {
      console.warn("[MetaSignup DEBUG] Onboarding session expired!");
      emitTerminalResult({ event: "session_expired", data: {} });
      return;
    }

    const normalizedResult = {
      event: "completed",
      data: {
        waba_id: s.signupData?.waba_id || "",
        phone_number_id: s.signupData?.phone_number_id || "",
        business_id: s.signupData?.business_id || "",
        ...(s.signupData || {}),
      },
    };

    const completionPayload = {
      onboarding_session_id: s.docliftOnboardingSessionId,
      session_token: s.docliftSessionToken,
      authorization_code: s.authCode || null,
      mode: s.mode || (isCoexistence ? "coexistence" : "standard"),
      session_info: {
        waba_id: normalizedResult.data.waba_id || null,
        phone_number_id: normalizedResult.data.phone_number_id || null,
        business_id: normalizedResult.data.business_id || null,
      },
    };

    // console.log("[MetaSignup DEBUG] >>> Calling backend onComplete with payload:", completionPayload);

    try {
      let completeResponse = null;
      if (onComplete) {
        completeResponse = await onComplete(completionPayload);
      }
      // console.log("[MetaSignup DEBUG] >>> onComplete succeeded! Backend response:", completeResponse);
      terminalEventRef.current = true;
      // console.log("[MetaSignup DEBUG] >>> Terminal event emitted to onResult:", normalizedResult);
      onResult?.(normalizedResult);
    } catch (error) {
      console.error("[MetaSignup DEBUG] >>> onComplete failed with error:", error);
      terminalEventRef.current = true;
      onResult?.({ event: "error", data: {} });
    } finally {
      cleanup();
    }
  }, [cleanup, emitTerminalResult, onComplete, onResult]);

  const startPopupClosedMonitor = useCallback(() => {
    // console.log("[MetaSignup DEBUG] startPopupClosedMonitor called. hasPopup:", !!popupWindowRef.current);
    if (!popupWindowRef.current || popupCheckRef.current) return;

    popupCheckRef.current = window.setInterval(() => {
      if (!popupWindowRef.current?.closed) return;

      console.warn("[MetaSignup DEBUG] Popup closure detected by polling monitor! State:", {
        onboardingSessionId: sessionRef.current.docliftOnboardingSessionId,
        mode: sessionRef.current.mode,
        isCoexistence: sessionRef.current.isCoexistence,
        hasSignupData: !!sessionRef.current.signupData,
        signupData: sessionRef.current.signupData,
        hasAuthCode: !!sessionRef.current.authCode,
        authCode: sessionRef.current.authCode,
        submitted: sessionRef.current.submitted,
        terminalEvent: terminalEventRef.current,
      });

      // Stop polling interval immediately
      window.clearInterval(popupCheckRef.current);
      popupCheckRef.current = null;

      // Allow 1500ms grace period for trailing postMessage or FB.login callback
      window.setTimeout(() => {
        console.log("[MetaSignup DEBUG] Post-closure grace timeout fired. Current state:", {
          hasSignupData: !!sessionRef.current.signupData,
          signupData: sessionRef.current.signupData,
          hasAuthCode: !!sessionRef.current.authCode,
          submitted: sessionRef.current.submitted,
          terminalEvent: terminalEventRef.current,
        });

        if (terminalEventRef.current || sessionRef.current.submitted) {
          console.log("[MetaSignup DEBUG] Terminal or already submitted, ignoring popup close.");
          return;
        }

        // If authCode was received (even without signupData)
        if (sessionRef.current.authCode) {
          console.log("[MetaSignup DEBUG] Popup closed and authCode is available! Triggering trySubmit!");
          if (submitGraceTimerRef.current) {
            window.clearTimeout(submitGraceTimerRef.current);
            submitGraceTimerRef.current = null;
          }
          trySubmit({ allowWithoutSignupData: true });
          return;
        }

        // If signup data was received (without authCode)
        if (sessionRef.current.signupData) {
          console.log("[MetaSignup DEBUG] Signup data captured before/during popup close. Triggering trySubmit!");
          if (submitGraceTimerRef.current) {
            window.clearTimeout(submitGraceTimerRef.current);
            submitGraceTimerRef.current = null;
          }
          trySubmit({ allowWithoutAuthCode: true });
          return;
        }

        console.warn("[MetaSignup DEBUG] Popup closed without signupData or authCode. Emitting popup_closed.");
        emitTerminalResult({ event: "popup_closed", data: {} });
      }, 1500);
    }, 500);
  }, [emitTerminalResult, trySubmit]);

  // ─── Auth code handler ────────────────────────────────────────────────────
  const handleAuthCallback = useCallback((code) => {
    console.log("[MetaSignup DEBUG] handleAuthCallback called with code:", code);
    if (terminalEventRef.current || !code) return;
    sessionRef.current.authCode = code;

    // If signupData is already present, submit immediately!
    if (sessionRef.current.signupData) {
      console.log("[MetaSignup DEBUG] Both authCode and signupData present, submitting immediately!");
      if (submitGraceTimerRef.current) {
        window.clearTimeout(submitGraceTimerRef.current);
        submitGraceTimerRef.current = null;
      }
      trySubmit();
    } else {
      // Start a grace window (1500ms) for postMessage signupData to arrive.
      // If timer expires or popup closes, submit with authCode!
      console.log("[MetaSignup DEBUG] authCode received. Waiting up to 1500ms for postMessage signupData...");
      if (!submitGraceTimerRef.current) {
        submitGraceTimerRef.current = window.setTimeout(() => {
          submitGraceTimerRef.current = null;
          console.log("[MetaSignup DEBUG] PostMessage grace timer expired. Submitting with authCode!");
          trySubmit({ allowWithoutSignupData: true });
        }, 1500);
      }
    }
  }, [trySubmit]);

  // ─── Session postMessage listener (WABA ID, Phone ID, Business ID) ────────
  useEffect(() => {
    const handleMessage = (event) => {
      const isTrusted = isPermittedOrigin(event.origin);
      console.log("[MetaSignup DEBUG] Window postMessage received:", {
        origin: event.origin,
        isTrustedOrigin: isTrusted,
        dataType: typeof event.data,
        data: event.data,
      });

      if (!isTrusted) {
        console.warn("[MetaSignup DEBUG] Ignored message from untrusted origin:", event.origin);
        return;
      }

      // Check if this is an SDK URL-encoded message containing code=
      if (typeof event.data === "string" && event.data.includes("code=")) {
        try {
          const params = new URLSearchParams(event.data);
          const code = params.get("code");
          if (code && !sessionRef.current.authCode) {
            console.log("[MetaSignup DEBUG] Extracted OAuth code from postMessage URL params:", code);
            handleAuthCallback(code);
          }
        } catch { }
      }

      let data;
      if (typeof event.data === "string") {
        try {
          data = JSON.parse(event.data);
        } catch {
          data = event.data;
        }
      } else {
        data = event.data;
      }

      const isMeta = isMetaSignupPayload(data);
      console.log("[MetaSignup DEBUG] isMetaSignupPayload:", isMeta, "data:", data);

      if (!isMeta) {
        return;
      }

      const normalized = normalise(data);
      console.log("[MetaSignup DEBUG] Normalized postMessage event:", normalized);

      if (normalized.event === "completed") {
        sessionRef.current.signupData = normalized.data;
        console.log("[MetaSignup DEBUG] Stored signupData in sessionRef:", sessionRef.current.signupData);

        if (sessionRef.current.authCode) {
          console.log("[MetaSignup DEBUG] Both authCode and signupData present, submitting immediately!");
          trySubmit();
        } else if (sessionRef.current.isCoexistence) {
          console.log("[MetaSignup DEBUG] Coexistence mode: starting 1500ms grace window for authCode before submission...");
          if (!submitGraceTimerRef.current) {
            submitGraceTimerRef.current = window.setTimeout(() => {
              console.log("[MetaSignup DEBUG] Coexistence grace window ended. Submitting now with signupData!");
              submitGraceTimerRef.current = null;
              trySubmit({ allowWithoutAuthCode: true });
            }, 1500);
          }
        } else {
          console.log("[MetaSignup DEBUG] Standard mode: waiting for authCode before submission.");
          trySubmit();
        }
        return;
      }

      if (normalized.event === "ignored") {
        return;
      }

      console.warn("[MetaSignup DEBUG] Terminal normalized event received from postMessage:", normalized);
      emitTerminalResult(normalized);
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [emitTerminalResult, handleAuthCallback, trySubmit]);

  useEffect(() => cleanup, [cleanup]);

  // ─── Launch Embedded Signup ───────────────────────────────────────────────
  const openSignup = useCallback(async (launch, context = {}) => {
    cleanup();
    terminalEventRef.current = false;
    sessionRef.current.docliftOnboardingSessionId = context.onboardingSessionId || null;
    sessionRef.current.docliftSessionToken = context.sessionToken || null;
    sessionRef.current.expiresAt = context.expiresAt || null;

    const loginOptions = buildLoginOptions(launch, context);
    const isCoexistence =
      String(context?.mode || launch?.mode || "").toLowerCase() === "coexistence" ||
      loginOptions?.extras?.featureType === "whatsapp_business_app_onboarding";
    sessionRef.current.mode = isCoexistence ? "coexistence" : "standard";
    sessionRef.current.isCoexistence = isCoexistence;

    console.log("[MetaSignup DEBUG] openSignup initialized:", {
      onboardingSessionId: sessionRef.current.docliftOnboardingSessionId,
      mode: sessionRef.current.mode,
      isCoexistence: sessionRef.current.isCoexistence,
      loginOptions,
    });

    // Wait until FB.init() has completed (flag set in index.html fbAsyncInit).
    await waitForFBReady();

    if (!loginOptions.config_id) {
      emitTerminalResult({ event: "error", data: { code: "missing_config_id" } });
      return;
    }

    const originalWindowOpen = window.open;
    originalWindowOpenRef.current = originalWindowOpen;

    window.open = function patchedWindowOpen(...args) {
      console.log("[MetaSignup DEBUG] window.open intercepted! Args:", args);
      const popup = originalWindowOpen.apply(window, args);
      popupWindowRef.current = popup;
      console.log("[MetaSignup DEBUG] Popup window created:", !!popup);

      if (!popup) {
        console.warn("[MetaSignup DEBUG] Popup window was blocked by browser!");
        window.setTimeout(() => emitTerminalResult({ event: "popup_blocked", data: {} }), 0);
        return popup;
      }

      startPopupClosedMonitor();
      return popup;
    };

    try {
      startTimeout();
      console.log("[MetaSignup] sanitized FB.login options:", {
        config_id: loginOptions.config_id,
        response_type: loginOptions.response_type,
        override_default_response_type: loginOptions.override_default_response_type,
        extras: loginOptions.extras,
      });

      window.FB.login(
        (response) => {
          console.log("[MetaSignup DEBUG] FB.login response callback fired:", {
            response,
            currentSessionState: {
              mode: sessionRef.current.mode,
              isCoexistence: sessionRef.current.isCoexistence,
              hasSignupData: !!sessionRef.current.signupData,
              signupData: sessionRef.current.signupData,
              hasAuthCode: !!sessionRef.current.authCode,
              authCode: sessionRef.current.authCode,
            },
          });

          if (response?.authResponse) {
            // 1. Process OAuth Authorization Code
            if (response.authResponse.code) {
              console.log("[MetaSignup DEBUG] OAuth code in authResponse:", response.authResponse.code);
              handleAuthCallback(response.authResponse.code);
            }

            // 2. Fallback: If signupData wasn't received via postMessage (e.g., blocked by privacy extensions),
            // attempt extraction from FB.login response payload
            if (!sessionRef.current.signupData) {
              const payloads = collectPayloadObjects(response.authResponse);
              const wabaId = pickPayloadValue(payloads, "waba_id", "wabaId", "wabaID", "waba");
              const phoneNumberId = pickPayloadValue(payloads, "phone_number_id", "phoneNumberId", "phone_id", "phoneId");
              const businessId = pickPayloadValue(payloads, "business_id", "businessId", "businessID");

              console.log("[MetaSignup DEBUG] Checking FB.login authResponse fallback for WABA data:", {
                wabaId,
                phoneNumberId,
                businessId,
              });

              if (wabaId || phoneNumberId) {
                sessionRef.current.signupData = {
                  waba_id: wabaId,
                  phone_number_id: phoneNumberId,
                  business_id: businessId,
                  meta_event: "FB_LOGIN_AUTH_RESPONSE",
                };
                console.log("[MetaSignup DEBUG] Fallback extracted signupData:", sessionRef.current.signupData);
                trySubmit();
              }
            }
          } else {
            console.warn("[MetaSignup DEBUG] FB.login response has no authResponse. Status:", response?.status);
            // If signupData was already received (e.g. via FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING postMessage),
            // the user successfully finished the embedded flow and closed the popup:
            if (sessionRef.current.signupData) {
              console.log("[MetaSignup DEBUG] Signup data present when FB.login returned null authResponse. Finalizing submission!");
              if (submitGraceTimerRef.current) {
                window.clearTimeout(submitGraceTimerRef.current);
                submitGraceTimerRef.current = null;
              }
              trySubmit({ allowWithoutAuthCode: true });
            } else {
              console.warn("[MetaSignup DEBUG] No signupData and no authResponse. Emitting cancelled.");
              emitTerminalResult({ event: "cancelled", data: {} });
            }
          }
        },
        loginOptions
      );
    } catch (error) {
      console.error("[MetaSignup DEBUG] FB.login failed to launch:", error);
      emitTerminalResult({ event: "popup_blocked", data: {} });
    } finally {
      // Keep patchedWindowOpen around for 5s to catch any async popup creation by FB SDK
      window.setTimeout(() => {
        if (originalWindowOpenRef.current && window.open !== originalWindowOpenRef.current) {
          window.open = originalWindowOpenRef.current;
        }
      }, 5000);
    }
  }, [cleanup, emitTerminalResult, handleAuthCallback, startPopupClosedMonitor, startTimeout, trySubmit]);

  return { openSignup, handleAuthCallback };
}
